import * as React from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Coins,
  Copy,
  Layers,
  Pencil,
  Plus,
  ReceiptText,
  Trash2,
  X
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { PHONE_DIALOG_FOOTER, PHONE_SHEET_DIALOG } from "@/lib/responsive";
import { cn } from "@/lib/utils";
import { Combobox } from "@/components/Combobox";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { AmountInput } from "@/components/admin/AmountInput";
import SegmentedTabs from "@/components/SegmentedTabs";
import { WizardStepper } from "@/components/requests/WizardStepper";
import { COUNTRIES, CURRENCIES, currencyForCountry } from "@/lib/countries";
import {
  ApiError,
  CategoryRemovalUsage,
  fetchDesignations,
  fetchExpenseCategories,
  fetchPolicy,
  previewCategoryRemoval,
  updateExpenseCategories,
  updatePolicy
} from "../../api";
import {
  CategoryLimits,
  CountryAmount,
  CountryLimits,
  FixedAllowanceBucket,
  FixedAllowanceEntry,
  PolicyScheme,
  ReimbursementBucket,
  ReimbursementEntry,
  TRAVEL_TYPE_LABEL,
  TRAVEL_TYPES,
  TravelType
} from "../../types";

// Read by every component below that needs the admin-configured expense-
// category list (RateFields, TravellerPickStep, the Default/Fixed/
// Reimbursement limits flows, etc.) - a plain prop would mean drilling it
// through 4-5 layers of wizard-step components that otherwise have no
// reason to know about it at all. Always provided by Policies itself (the
// default [] only matters for a component rendered outside it, which
// doesn't happen in this file).
const CategoriesContext = React.createContext<string[]>([]);
function useCategories(): string[] {
  return React.useContext(CategoriesContext);
}

// New rates start every category as "Limit" with 0 - an admin chooses "No
// limit" (null) per category explicitly; see CategoryLimits in types.ts.
// `categories` is the admin-configured list (fetchExpenseCategories) - see
// this file's own "Expense Categories" management section below.
function emptyLimits(categories: string[]): CategoryLimits {
  return Object.fromEntries(categories.map((c) => [c, 0]));
}

function emptyFixedBucket(): FixedAllowanceBucket {
  return { countries: [] };
}

function emptyReimbursementBucket(): ReimbursementBucket {
  return { countries: [] };
}

const SCHEME_LABEL: Record<PolicyScheme, string> = {
  fixed_allowance: "Fixed",
  reimbursement: "Reimbursement"
};

/** Designation picker that excludes designations already used in this
 *  table - a designation gets exactly one row per scheme. */
function DesignationSelect({
  options,
  used,
  value,
  onChange,
  disabled
}: {
  options: string[];
  used: string[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const available = options.filter((d) => d === value || !used.includes(d));
  return (
    <Combobox
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={available.map((d) => ({ value: d, label: d }))}
      placeholder="Select a designation…"
      searchPlaceholder="Search designations…"
    />
  );
}

function CountrySelect({
  used,
  value,
  onChange
}: {
  used: string[];
  value: string;
  onChange: (country: string) => void;
}) {
  const available = COUNTRIES.filter(
    (c) => c.name === value || !used.includes(c.name)
  );
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={available.map((c) => ({
        value: c.name,
        label: c.name,
        sublabel: c.currency
      }))}
      placeholder="Select a country…"
      searchPlaceholder="Search countries…"
    />
  );
}

function CurrencySelect({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={CURRENCIES.map((c) => ({ value: c, label: c }))}
      placeholder="Currency…"
      searchPlaceholder="Search currencies…"
    />
  );
}

// ── Country rates model ─────────────────────────────────────────────────
// A policy country row carries up to two rates: Domestic - a trip within
// that country - and International - a trip to that country from another
// one. The employee chooses Domestic or International (and the country)
// when raising a request or advance; nothing about where they live decides
// it (see db/policy_store.py). Together with the scheme and the designation
// that is the policy matrix: scheme x travel type x designation x country.
// Default Limits edits both schemes for a country
// (its Fixed row and its Reimbursement row live in two separate buckets on
// the policy); Fixed Limits / Reimbursement Limits edit one. The wizards
// work on one loose shape for either scheme and narrow back to
// CountryAmount / CountryLimits at the save boundary.
type AnyRate = {
  currency: string;
  amount?: number;
  limits?: CategoryLimits;
};
type SchemeRates = Partial<Record<TravelType, AnyRate>>;
type CountryDraft = {
  country: string;
  rates: Partial<Record<PolicyScheme, SchemeRates>>;
};

function ratesOf(
  row: CountryAmount | CountryLimits | undefined
): SchemeRates | undefined {
  if (!row) return undefined;
  const out: SchemeRates = {};
  for (const t of TRAVEL_TYPES) {
    const rate = row[t];
    if (rate) out[t] = { ...rate } as AnyRate;
  }
  return Object.keys(out).length ? out : undefined;
}

/** One draft per country across the given buckets, in first-seen order. */
function draftsFrom(
  buckets: {
    scheme: PolicyScheme;
    countries: (CountryAmount | CountryLimits)[];
  }[]
): CountryDraft[] {
  const byCountry = new Map<string, CountryDraft>();
  for (const { scheme, countries } of buckets) {
    for (const row of countries) {
      const draft = byCountry.get(row.country) ?? {
        country: row.country,
        rates: {}
      };
      draft.rates[scheme] = ratesOf(row);
      byCountry.set(row.country, draft);
    }
  }
  return [...byCountry.values()];
}

function rowFor(
  draft: CountryDraft,
  scheme: PolicyScheme,
  categories: string[]
): CountryAmount | CountryLimits | null {
  const rates = draft.rates[scheme];
  if (!rates || !TRAVEL_TYPES.some((t) => rates[t])) return null;
  const row: Record<string, unknown> = { country: draft.country };
  for (const t of TRAVEL_TYPES) {
    const rate = rates[t];
    if (!rate) continue;
    row[t] =
      scheme === "fixed_allowance"
        ? { currency: rate.currency, amount: rate.amount ?? 0 }
        : {
          currency: rate.currency,
          limits: { ...emptyLimits(categories), ...rate.limits }
        };
  }
  return row as unknown as CountryAmount | CountryLimits;
}

/** A bucket's country list with `draft`'s row for `scheme` put in place -
 *  replacing the existing row for that country (keeping its position),
 *  appending a new one, or dropping it when the draft offers no rate for
 *  this scheme. */
function withCountry<T extends CountryAmount | CountryLimits>(
  countries: T[],
  draft: CountryDraft,
  scheme: PolicyScheme,
  categories: string[]
): T[] {
  const row = rowFor(draft, scheme, categories) as T | null;
  const index = countries.findIndex((c) => c.country === draft.country);
  if (index === -1) return row ? [...countries, row] : countries;
  if (!row) return countries.filter((_, i) => i !== index);
  return countries.map((c, i) => (i === index ? row : c));
}

/** `draft` with one traveller type's rates replaced, per scheme (undefined
 *  = that rate is switched off / not offered). */
function withTravelRates(
  draft: CountryDraft,
  travelType: TravelType,
  rates: Partial<Record<PolicyScheme, AnyRate | undefined>>
): CountryDraft {
  const next: CountryDraft = {
    country: draft.country,
    rates: { ...draft.rates }
  };
  for (const scheme of Object.keys(rates) as PolicyScheme[]) {
    next.rates[scheme] = { ...next.rates[scheme], [travelType]: rates[scheme] };
  }
  return next;
}

function hasAnyRate(draft: CountryDraft, schemes: PolicyScheme[]): boolean {
  return schemes.some((s) => TRAVEL_TYPES.some((t) => draft.rates[s]?.[t]));
}

function formatRate(scheme: PolicyScheme, rate: AnyRate, categories: string[]): string {
  if (scheme === "fixed_allowance")
    return `${rate.currency} ${(rate.amount ?? 0).toLocaleString()} / day`;
  const noLimit = categories.filter(
    (c) => rate.limits?.[c] === null
  ).length;
  const limited = categories.length - noLimit;
  const total = categories.reduce(
    (sum, c) => sum + (rate.limits?.[c] ?? 0),
    0
  );
  const limitedText = `${rate.currency} · ${total.toLocaleString()} across ${limited} ${limited === 1 ? "category" : "categories"}`;
  return noLimit === 0
    ? limitedText
    : limited === 0
      ? `${rate.currency} · no limit on any category`
      : `${limitedText} · ${noLimit} no limit`;
}

function audienceText(travelType: TravelType, country: string): string {
  return travelType === "domestic"
    ? `Trips within ${country} - the start and end city are both in ${country}.`
    : `Trips to ${country} from another country.`;
}

// ── Wizard building blocks ──────────────────────────────────────────────

function StepHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="space-y-0.5">
      <p className="text-sm font-semibold">{title}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function WizardFooter({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  backLabel = "Back"
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  backLabel?: string;
}) {
  return (

    <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
      {onBack ? (
        <Button
          variant="outline"
          className="w-full sm:w-auto sm:min-w-[7.5rem]"
          onClick={onBack}
        >
          <ArrowLeft className="mr-1.5 h-4 w-4 shrink-0" />
          {backLabel}
        </Button>
      ) : (
        <div className="hidden sm:block" />
      )}
      {onNext && (
        <Button
          onClick={onNext}
          className="w-full sm:w-auto sm:min-w-[7.5rem]"
          disabled={nextDisabled}
        >
          {nextLabel ?? "Next"}
          <ArrowRight className="ml-1.5 h-4 w-4 shrink-0" />
        </Button>
      )}
    </div>
  );
}

/** Confirmation shown after a rate is saved, pointing at the natural next
 *  thing to do. Behaves like the Sonner toasts elsewhere in the app -
 *  auto-dismisses itself after a few seconds - since it previously stuck
 *  around until the page was refreshed (reported bug: a banner from a
 *  save minutes ago was still showing). Also closeable immediately via
 *  the × so it doesn't block a reader who wants it gone sooner. */
function Notice({
  children,
  onDismiss
}: {
  children: React.ReactNode;
  onDismiss: () => void;
}) {
  React.useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="flex-1">{children}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-sm text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Selectable card used for every choice step - a radio-style pick
 *  (designation, country) or, with `multi`, a checkbox (traveller types,
 *  where Domestic and International can both be chosen). */
function ChoiceCard({
  selected,
  onClick,
  children,
  trailing,
  multi = false
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  trailing?: React.ReactNode;
  multi?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border p-3 transition-colors",
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "hover:bg-muted/60"
      )}
    >
      <button
        type="button"
        onClick={onClick}
        {...(multi
          ? { role: "checkbox", "aria-checked": selected }
          : { "aria-pressed": selected })}
        className="flex min-w-0 flex-1 items-start gap-3 text-left focus-visible:outline-none"
      >
        <span
          className={cn(
            "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border",
            multi ? "rounded-[0.25rem]" : "rounded-full",
            selected
              ? "border-primary bg-primary text-primary-foreground"
              : "border-muted-foreground/40"
          )}
        >
          {selected && <Check className="h-3 w-3" />}
        </span>
        <span className="min-w-0 flex-1">{children}</span>
      </button>
      {trailing}
    </div>
  );
}

/** Full read-only view of a configured country: every traveller type ×
 *  scheme, with the Fixed amount or each Reimbursement category limit. */
function CountryDetails({
  draft,
  schemes
}: {
  draft: CountryDraft;
  schemes: PolicyScheme[];
}) {
  const categories = useCategories();
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {TRAVEL_TYPES.map((t, i) => (
        <div key={t} className="space-y-2 rounded-md bg-muted/40 p-3">
          <div>
            <p className="text-sm font-semibold">
              ({i + 1}) {TRAVEL_TYPE_LABEL[t]}
            </p>
            <p className="text-xs text-muted-foreground">
              {audienceText(t, draft.country)}
            </p>
          </div>
          {schemes.map((s) => {
            const rate = draft.rates[s]?.[t];
            return (
              <div key={s} className="space-y-1">
                <p className="flex items-center gap-1.5 text-xs font-medium">
                  {s === "fixed_allowance" ? (
                    <Banknote className="h-3.5 w-3.5 text-primary" />
                  ) : (
                    <Coins className="h-3.5 w-3.5 text-primary" />
                  )}
                  {SCHEME_LABEL[s]}
                </p>
                {!rate ? (
                  <p className="text-xs text-muted-foreground">Not offered</p>
                ) : s === "fixed_allowance" ? (
                  <p className="text-sm tabular-nums">
                    {rate.currency} {(rate.amount ?? 0).toLocaleString()}{" "}
                    <span className="text-xs text-muted-foreground">
                      per day
                    </span>
                  </p>
                ) : (
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                    {categories.map((c) => (
                      <React.Fragment key={c}>
                        <dt className="text-muted-foreground">
                          {c}
                        </dt>
                        <dd className="text-right tabular-nums">
                          {rate.limits?.[c] === null ? (
                            <span className="text-muted-foreground">
                              No limit
                            </span>
                          ) : (
                            <>
                              {rate.currency}{" "}
                              {(rate.limits?.[c] ?? 0).toLocaleString()}
                            </>
                          )}
                        </dd>
                      </React.Fragment>
                    ))}
                  </dl>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Step: (1) Domestic and/or (2) International for the chosen country -
 *  checkboxes, so both can be set up in one pass. Ticking a type only ever
 *  ADDS it to this editing pass (to set up or change its rate on the next
 *  step) - it's never how an already-saved type gets removed, so leaving a
 *  configured type unticked (e.g. because this pass is only adding the
 *  OTHER type) never touches its existing rate. Removing a configured
 *  type's rate is instead its own explicit action (the "Remove rate"
 *  button below), independent of the tick. */
function TravellerPickStep({
  draft,
  schemes,
  value,
  onChange,
  onRemoveType,
  removingType
}: {
  draft: CountryDraft;
  schemes: PolicyScheme[];
  value: TravelType[];
  onChange: (t: TravelType[]) => void;
  /** Deletes this type's rate(s) immediately (saves on its own - see the
   *  confirm dialog below), independent of what's ticked. */
  onRemoveType: (t: TravelType) => void;
  /** The type currently being removed, for its own pending state. */
  removingType: TravelType | null;
}) {
  const categories = useCategories();
  const [confirmRemove, setConfirmRemove] = React.useState<TravelType | null>(
    null
  );
  const toggle = (t: TravelType) =>
    onChange(
      value.includes(t)
        ? value.filter((x) => x !== t)
        : TRAVEL_TYPES.filter((x) => x === t || value.includes(x))
    );
  return (
    <div className="space-y-4">
      <StepHeading
        title={`Which trips does ${draft.country} cover?`}
        hint="Tick which trip types to set up or change now - one or both. Leaving a configured type unticked keeps its existing rate untouched; use Remove rate to delete one."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {TRAVEL_TYPES.map((t, i) => {
          const configured = schemes.filter((s) => draft.rates[s]?.[t]);
          return (
            <ChoiceCard
              key={t}
              multi
              selected={value.includes(t)}
              onClick={() => toggle(t)}
              trailing={
                configured.length > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 px-2 text-xs text-destructive hover:text-destructive"
                    disabled={removingType === t}
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmRemove(t);
                    }}
                  >
                    {removingType === t ? "Removing…" : "Remove rate"}
                  </Button>
                ) : undefined
              }
            >
              <span className="block text-sm font-medium">
                ({i + 1}) {TRAVEL_TYPE_LABEL[t]}
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {audienceText(t, draft.country)}
              </span>
              <span className="mt-2 block text-xs">
                {configured.length === 0 ? (
                  <span className="text-muted-foreground">Not set yet</span>
                ) : (
                  configured.map((s) => (
                    <span key={s} className="block tabular-nums">
                      {schemes.length > 1 && `${SCHEME_LABEL[s]}: `}
                      {formatRate(s, draft.rates[s]![t]!, categories)}
                    </span>
                  ))
                )}
              </span>
            </ChoiceCard>
          );
        })}
      </div>
      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(open) => !open && setConfirmRemove(null)}
        title={
          confirmRemove
            ? `Remove the ${TRAVEL_TYPE_LABEL[confirmRemove]} rate for ${draft.country}?`
            : "Remove this rate?"
        }
        description="This deletes it right away - separate from anything ticked above, and from any other type's rate for this country."
        confirmLabel="Remove rate"
        destructive
        pending={!!confirmRemove && removingType === confirmRemove}
        onConfirm={() => {
          if (confirmRemove) onRemoveType(confirmRemove);
          setConfirmRemove(null);
        }}
      />
    </div>
  );
}

function RateFields({
  scheme,
  rate,
  onChange
}: {
  scheme: PolicyScheme;
  rate: AnyRate;
  onChange: (rate: AnyRate) => void;
}) {
  const categories = useCategories();
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Currency</Label>
          <CurrencySelect
            value={rate.currency}
            onChange={(currency) => onChange({ ...rate, currency })}
          />
        </div>
        {scheme === "fixed_allowance" && (
          <div className="space-y-1.5">
            <Label>Allowance amount (per day)</Label>
            <AmountInput
              value={rate.amount ?? 0}
              onChange={(amount) => onChange({ ...rate, amount })}
            />
          </div>
        )}
      </div>
      {scheme === "reimbursement" && (
        // Each category is either "Limit" (a per-day amount, as before) or
        // "No limit" (null - no cap and never an over-limit warning; see
        // CategoryLimits in types.ts). Switching back to Limit starts at 0.
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => {
            const value = rate.limits?.[category];
            const setLimit = (v: number | null) =>
              onChange({
                ...rate,
                limits: { ...emptyLimits(categories), ...rate.limits, [category]: v }
              });
            return (
              <div key={category} className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label>{category}</Label>
                  <SegmentedTabs
                    value={value === null ? "none" : "limit"}
                    onChange={(mode) => setLimit(mode === "none" ? null : 0)}
                    options={[
                      { value: "limit", label: "Limit" },
                      { value: "none", label: "No limit" }
                    ]}
                    className="h-7 w-[6.5rem] text-xs"
                  />
                </div>
                {value === null ? (
                  <div className="flex h-10 items-center rounded-md border border-dashed px-3 text-sm text-muted-foreground">
                    No limit - any amount can be claimed
                  </div>
                ) : (
                  <AmountInput
                    value={value ?? 0}
                    onChange={(v) => setLimit(v)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Rates entered for each selected traveller type, per scheme (undefined =
 *  that rate is switched off / not offered). */
type RatesByTravel = Partial<
  Record<TravelType, Partial<Record<PolicyScheme, AnyRate | undefined>>>
>;

/** True iff any rate actually being saved (i.e. still offered — an
 *  undefined rate means "not offered," not "zero," and is skipped) has a
 *  0 amount, or a 0 in any of its reimbursement category limits. Doesn't
 *  block the save — only decides whether to confirm first (see
 *  RatesStep's own Save button). */
function hasZeroAmount(toSave: RatesByTravel): boolean {
  for (const byScheme of Object.values(toSave)) {
    for (const rate of Object.values(byScheme ?? {})) {
      if (!rate) continue;
      if (rate.amount === 0) return true;
      if (rate.limits && Object.values(rate.limits).some((v) => v === 0))
        return true;
    }
  }
  return false;
}

/** Final step: the rate(s) for the chosen country, for every selected
 *  traveller type - (1) Fixed and (2) Reimbursement for Default Limits,
 *  just the one scheme for the designation flows. Each has an Offer
 *  switch: off means that rate isn't offered, so the country isn't listed
 *  for that trip type. */
function RatesStep({
  schemes,
  draft,
  travelTypes,
  saving,
  onBack,
  onSave
}: {
  schemes: PolicyScheme[];
  draft: CountryDraft;
  travelTypes: TravelType[];
  saving: boolean;
  onBack: () => void;
  onSave: (rates: RatesByTravel) => void;
}) {
  const categories = useCategories();
  // Confirmed before every Save rates click, zero-amount or not — see the
  // ConfirmDialog below, whose wording adapts to hasZeroAmount(toSave).
  const [confirmSaveOpen, setConfirmSaveOpen] = React.useState(false);

  // Read once — the parent re-keys this step per country + traveller
  // selection, so re-renders (the drafts are re-derived on every render)
  // never reset what the admin has typed.
  const [initial] = React.useState<RatesByTravel>(() => {
    const out: RatesByTravel = {};
    for (const t of travelTypes) {
      out[t] = {};
      for (const s of schemes) out[t]![s] = draft.rates[s]?.[t];
    }
    return out;
  });
  const [rates, setRates] = React.useState<RatesByTravel>(initial);
  const setRate = (t: TravelType, s: PolicyScheme, rate: AnyRate | undefined) =>
    setRates((r) => ({ ...r, [t]: { ...r[t], [s]: rate } }));

  const defaultRate = (scheme: PolicyScheme): AnyRate => {
    const currency = currencyForCountry(draft.country) || "USD";
    return scheme === "fixed_allowance"
      ? { currency, amount: 0 }
      : { currency, limits: emptyLimits(categories) };
  };

  // A type not ticked on the previous step is simply not part of this
  // editing pass - its existing rate (if any) is left exactly as saved.
  // Removing a type's rate is its own explicit action on the previous step
  // (TravellerPickStep's "Remove rate" button), not inferred from the tick.
  const toSave: RatesByTravel = rates;

  const isDirty = JSON.stringify(rates) !== JSON.stringify(initial);
  const after = applyRates(draft, toSave);
  const removesCountry = !hasAnyRate(after, schemes);

  return (
    <div className="space-y-4">
      <StepHeading
        title={`Rates for ${draft.country}`}
        hint={
          travelTypes.length > 1
            ? "Domestic and International rates are saved together."
            : audienceText(travelTypes[0], draft.country)
        }
      />
      <div className="space-y-5">
        {travelTypes.map((t) => {
          const other: TravelType =
            t === "domestic" ? "international" : "domestic";
          return (
            <section key={t} className="space-y-3">
              {travelTypes.length > 1 && (
                <div>
                  <p className="text-sm font-semibold">
                    ({TRAVEL_TYPES.indexOf(t) + 1}) {TRAVEL_TYPE_LABEL[t]}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {audienceText(t, draft.country)}
                  </p>
                </div>
              )}
              {schemes.map((scheme, i) => {
                const rate = rates[t]?.[scheme];
                // Copy from the other traveller type - what's typed here if
                // it's being edited too, otherwise its saved rate.
                const otherRate = travelTypes.includes(other)
                  ? rates[other]?.[scheme]
                  : draft.rates[scheme]?.[other];
                const switchId = `offer-${t}-${scheme}`;
                return (
                  <div key={scheme} className="space-y-3 rounded-md border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        {scheme === "fixed_allowance" ? (
                          <Banknote className="h-4 w-4 shrink-0 text-primary" />
                        ) : (
                          <Coins className="h-4 w-4 shrink-0 text-primary" />
                        )}
                        <span className="truncate text-sm font-medium">
                          {schemes.length > 1 && `(${i + 1}) `}
                          {SCHEME_LABEL[scheme]}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <Label
                          htmlFor={switchId}
                          className="text-xs text-muted-foreground"
                        >
                          Offer
                        </Label>
                        <Switch
                          id={switchId}
                          checked={!!rate}
                          onCheckedChange={(on) =>
                            setRate(
                              t,
                              scheme,
                              on ? defaultRate(scheme) : undefined
                            )
                          }
                          className="scale-90 sm:scale-100"
                        />
                      </div>
                    </div>
                    {otherRate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() =>
                          setRate(
                            t,
                            scheme,
                            JSON.parse(JSON.stringify(otherRate))
                          )
                        }
                      >
                        <Copy className="mr-1 h-3 w-3" /> Copy from{" "}
                        {TRAVEL_TYPE_LABEL[other]}
                      </Button>
                    )}
                    {rate ? (
                      <RateFields
                        scheme={scheme}
                        rate={rate}
                        onChange={(r) => setRate(t, scheme, r)}
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Not offered - {draft.country} won't be listed for{" "}
                        {TRAVEL_TYPE_LABEL[t]} trips under{" "}
                        {SCHEME_LABEL[scheme]}.
                      </p>
                    )}
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
      {removesCountry && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          With no rate offered for anyone, saving removes {draft.country} from
          this list.
        </p>
      )}
      <div className="flex items-center justify-between gap-3 border-t pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
        </Button>
        <Button
          disabled={saving || !isDirty}
          onClick={() => setConfirmSaveOpen(true)}
        >
          {saving ? "Saving…" : "Save rates"}
        </Button>
      </div>
      <ConfirmDialog
        open={confirmSaveOpen}
        onOpenChange={setConfirmSaveOpen}
        title={
          hasZeroAmount(toSave)
            ? "Save with a 0 amount?"
            : `Save rates for ${draft.country}?`
        }
        description={
          hasZeroAmount(toSave)
            ? "One or more rates being saved have a 0 amount or a 0 category limit. This is allowed, but double-check it's intentional and not an unfinished field before continuing."
            : "Double-check the amounts above before continuing — this updates the live policy."
        }
        confirmLabel={hasZeroAmount(toSave) ? "Save anyway" : "Save rates"}
        pending={saving}
        onConfirm={() => {
          setConfirmSaveOpen(false);
          onSave(toSave);
        }}
      />
    </div>
  );
}

/** Steps that can be jumped to via the stepper: only the ones already
 *  reached (a later step needs the earlier choices). */
function jumpBack(current: number, setStep: (n: number) => void) {
  return (n: number) => {
    if (n < current) setStep(n);
  };
}

/** Applies every selected traveller type's rates to `draft`. */
function applyRates(draft: CountryDraft, rates: RatesByTravel): CountryDraft {
  return (Object.keys(rates) as TravelType[]).reduce(
    (d, t) => withTravelRates(d, t, rates[t] ?? {}),
    draft
  );
}

/** After a save: which traveller types still have no rate at all (under
 *  `schemes`) - the natural next thing to set up. */
function unconfiguredTravelTypes(
  draft: CountryDraft,
  schemes: PolicyScheme[]
): TravelType[] {
  return TRAVEL_TYPES.filter((t) => !schemes.some((s) => draft.rates[s]?.[t]));
}

function savedLabel(types: TravelType[]): string {
  return types.map((t) => TRAVEL_TYPE_LABEL[t]).join(" and ");
}

// ── Flow position in the URL ────────────────────────────────────────────
// Each limits section opens on an OVERVIEW of everything configured (no
// `step` param). Adding or changing something starts a stage-by-stage
// flow (`step` = 1…n) whose timeline shows the stages actually completed.
// Where the admin is - step, designation, country, traveller types - lives
// in query params: moving forward pushes a history entry, so the
// page-header back arrow (lib/navigation.ts's previousStep) and the
// browser's Back both return to the previous stage (and from the first
// stage, to the overview), and a refresh keeps the place.
type FlowState = {
  step: number;
  designation: string | null;
  country: string | null;
  types: TravelType[];
};
const FLOW_PARAMS = ["step", "designation", "country", "types"] as const;
const OVERVIEW = 0;

function useFlowState(): [
  FlowState,
  (patch: Partial<FlowState>, push?: boolean) => void
] {
  const [params, setParams] = useSearchParams();
  const state: FlowState = {
    step: Math.max(0, Math.floor(Number(params.get("step"))) || 0),
    designation: params.get("designation"),
    country: params.get("country"),
    types: TRAVEL_TYPES.filter((t) =>
      (params.get("types") || "").split(",").includes(t)
    )
  };
  // One call per user action - two back-to-back calls would each start
  // from the same current URL, and the second would drop the first's change.
  const update = React.useCallback(
    (patch: Partial<FlowState>, push = false) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of Object.keys(patch) as (keyof FlowState)[]) {
            const value = patch[key];
            const text = Array.isArray(value)
              ? value.join(",")
              : value == null
                ? ""
                : String(value);
            if (!text || (key === "step" && text === "0")) next.delete(key);
            else next.set(key, text);
          }
          return next;
        },
        { replace: !push }
      );
    },
    [setParams]
  );
  return [state, update];
}

/** Traveller types a country already has any rate for - pre-ticked when
 *  its rates are opened for editing. */
function configuredTravelTypes(
  draft: CountryDraft | undefined,
  schemes: PolicyScheme[]
): TravelType[] {
  if (!draft) return [];
  return TRAVEL_TYPES.filter((t) => schemes.some((s) => draft.rates[s]?.[t]));
}

/** "Configuring: Manager · India · Domestic and International" - what the
 *  current stage applies to, so the choices made earlier stay visible. */
function FlowContext({ parts }: { parts: (string | null | undefined)[] }) {
  const shown = parts.filter(Boolean);
  if (!shown.length) return null;
  return (
    <p className="text-xs text-muted-foreground">
      Configuring:{" "}
      <span className="font-medium text-foreground">{shown.join(" · ")}</span>
    </p>
  );
}

/** Header row for an overview or a stage: title/hint on the left, the
 *  primary action (e.g. "+ Add Country") top-right. */
function SectionBar({
  title,
  hint,
  action
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <StepHeading title={title} hint={hint} />
      {action}
    </div>
  );
}

/** One configured country's full view inside a designation's dialog:
 *  its name, Edit / Remove, and every rate - Domestic and International
 *  side by side. */
function CountryOverviewRow({
  draft,
  schemes,
  onEdit,
  onRemove
}: {
  draft: CountryDraft;
  schemes: PolicyScheme[];
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">{draft.country}</p>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onEdit}
            aria-label={`Edit ${draft.country}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-red-700/20 dark:hover:bg-red-400/20 text-destructive hover:text-destructive"
            onClick={onRemove}
            aria-label={`Remove ${draft.country}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <CountryDetails draft={draft} schemes={schemes} />
    </div>
  );
}

/** Compact, clickable overview card (a designation or a country) that
 *  opens its full details in a dialog; remove sits beside it. */
function OverviewCard({
  title,
  subtitle,
  onOpen,
  onRemove,
  removeLabel
}: {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  onOpen: () => void;
  onRemove: () => void;
  removeLabel: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md p-3 transition-colors hover:bg-muted/40">
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-start gap-2 text-left focus-visible:outline-none"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-sm font-medium">
            {title}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
            {subtitle}
          </span>
        </span>
      </button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 flex items-center justify-center hover:bg-red-700/20 dark:hover:bg-red-400/20 text-destructive hover:text-destructive"
        onClick={onRemove}
        aria-label={removeLabel}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-primary hover:bg-primary/10 hover:text-primary"
        onClick={onOpen}
      >
        <ChevronRight className="h-3.5 w-3.5  " />
      </Button>
    </div>
  );
}

/** Per-traveller summary of a country's rates, in two columns. */
function CountrySummary({
  draft,
  schemes
}: {
  draft: CountryDraft;
  schemes: PolicyScheme[];
}) {
  const categories = useCategories();
  return (
    <span className="mt-1 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
      {TRAVEL_TYPES.map((t) => (
        <span key={t} className="min-w-0">
          <span className="font-medium text-foreground">
            {TRAVEL_TYPE_LABEL[t]}
          </span>
          {schemes.map((sch) => {
            const rate = draft.rates[sch]?.[t];
            return (
              <span key={sch} className="block truncate text-muted-foreground">
                {schemes.length > 1 && `${SCHEME_LABEL[sch]}: `}
                {rate ? (
                  <span className="tabular-nums text-foreground">
                    {formatRate(sch, rate, categories)}
                  </span>
                ) : (
                  "Not offered"
                )}
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
}

/** A configured country as a card - name + Domestic / International
 *  summary; clicking opens its full rates. */
function CountryCard({
  draft,
  schemes,
  onOpen,
  onRemove
}: {
  draft: CountryDraft;
  schemes: PolicyScheme[];
  onOpen: () => void;
  /** When provided, a delete (Trash) button is shown. Only the Default
   *  Limits section passes it - Fixed/Reimbursement Limits reuse this same
   *  card but without the per-country delete, so it's omitted there. */
  onRemove?: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2 rounded-md border p-3 transition-colors hover:bg-muted/40 cursor-pointer"
      onClick={onOpen}
    >
      <button
        type="button"
        aria-haspopup="dialog"
        className="flex min-w-0 flex-1 items-start gap-2 text-left focus-visible:outline-none"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{draft.country}</span>
          <CountrySummary draft={draft} schemes={schemes} />
        </span>
      </button>
      {onRemove && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 hover:bg-red-700/20 dark:hover:bg-red-400/20 text-destructive hover:text-destructive"
          // Stop the click bubbling to the card's own onClick (onOpen) -
          // otherwise deleting also opens the details dialog.
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${draft.country}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-primary hover:bg-primary/10 hover:text-primary"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

const DETAILS_DIALOG = cn(
  "max-h-[90dvh] overflow-y-auto sm:max-w-3xl",
  PHONE_SHEET_DIALOG
);

/** Stage "Country": the country being added (picked here) or edited
 *  (shown, already chosen). */
function CountryStage({
  drafts,
  schemes,
  country,
  onPick
}: {
  drafts: CountryDraft[];
  schemes: PolicyScheme[];
  country: string | null;
  onPick: (country: string) => void;
}) {
  const used = drafts.map((d) => d.country);
  const existing = country
    ? drafts.find((d) => d.country === country)
    : undefined;
  return (
    <div className="space-y-4">
      <StepHeading
        title={
          existing
            ? `Country: ${existing.country}`
            : "Choose the country to add"
        }
        hint={
          existing
            ? "Already configured - continue to change its rates."
            : "Pick the destination country. It's added once you save its first rate."
        }
      />
      {existing ? (
        <CountryDetails draft={existing} schemes={schemes} />
      ) : (
        <CountrySelect used={used} value={country ?? ""} onChange={onPick} />
      )}
    </div>
  );
}

// ── Default Limits: Country → Domestic | International → Fixed | Reimbursement
const DEFAULT_STEPS = [
  { label: "Country" },
  { label: "Domestic | International" },
  { label: "Fixed | Reimbursement" }
];
const BOTH_SCHEMES: PolicyScheme[] = ["fixed_allowance", "reimbursement"];

function DefaultLimitsFlow({
  drafts,
  saving,
  onSaveCountry,
  onRemoveCountry
}: {
  drafts: CountryDraft[];
  saving: boolean;
  onSaveCountry: (draft: CountryDraft) => Promise<boolean>;
  onRemoveCountry: (country: string) => Promise<boolean>;
}) {
  const [flow, setFlow] = useFlowState();
  const [notice, setNotice] = React.useState<React.ReactNode>(null);
  const [removing, setRemoving] = React.useState(false);
  const [removeTarget, setRemoveTarget] = React.useState<string | null>(null);
  // The configured country open in the details dialog.
  const [viewing, setViewing] = React.useState<string | null>(null);
  const viewingDraft = drafts.find((d) => d.country === viewing) ?? null;
  const { country, types: travelTypes } = flow;

  // A stage is only shown once the choices it needs exist (e.g. after a
  // refresh on a URL whose country was since removed).
  let step = Math.min(flow.step, DEFAULT_STEPS.length);
  if (step >= 2 && !country) step = 1;
  if (step >= 3 && travelTypes.length === 0) step = 2;

  const draft: CountryDraft | null = country
    ? (drafts.find((d) => d.country === country) ?? { country, rates: {} })
    : null;
  const toOverview = (message?: React.ReactNode) => {
    setNotice(message ?? null);
    setFlow({ step: OVERVIEW, country: null, types: [] }, true);
  };

  // Deletes one travel type's rate(s) for this country right away -
  // independent of whatever's ticked on the current step (see
  // TravellerPickStep's "Remove rate" button).
  const [removingType, setRemovingType] = React.useState<TravelType | null>(
    null
  );
  const removeType = async (t: TravelType) => {
    if (!draft) return;
    setRemovingType(t);
    try {
      const next = withTravelRates(
        draft,
        t,
        Object.fromEntries(BOTH_SCHEMES.map((s) => [s, undefined])) as Partial<
          Record<PolicyScheme, AnyRate | undefined>
        >
      );
      if (!(await onSaveCountry(next))) return;
      if (!hasAnyRate(next, BOTH_SCHEMES)) {
        toOverview(<>{draft.country} removed - no rates were left.</>);
        return;
      }
      setNotice(
        <>
          {TRAVEL_TYPE_LABEL[t]} rate removed for {draft.country}.
        </>
      );
      setFlow({ types: travelTypes.filter((x) => x !== t) }, true);
    } finally {
      setRemovingType(null);
    }
  };

  const save = async (rates: RatesByTravel) => {
    if (!draft) return;
    const next = applyRates(draft, rates);
    if (!(await onSaveCountry(next))) return;
    const saved = travelTypes;
    if (!hasAnyRate(next, BOTH_SCHEMES)) {
      toOverview(<>{draft.country} removed - no rates were left.</>);
      return;
    }
    const remaining = unconfiguredTravelTypes(next, BOTH_SCHEMES);
    if (remaining.length) {
      setNotice(
        <>
          <span className="font-medium">{savedLabel(saved)}</span> rates saved
          for {draft.country}. Set{" "}
          <span className="font-medium">{savedLabel(remaining)}</span> next, or
          go back to the overview.
        </>
      );
      setFlow({ step: 2, types: remaining }, true);
      return;
    }
    toOverview(
      <>
        <span className="font-medium">{savedLabel(saved)}</span> rates saved for{" "}
        {draft.country}.
      </>
    );
  };

  if (step === OVERVIEW) {
    return (
      <div className="space-y-4">
        {notice && <Notice onDismiss={() => setNotice(null)}>{notice}</Notice>}
        <SectionBar
          title="Configured countries"
          hint="Org-wide rates, used for any designation with no countries of its own."
          action={
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => {
                setNotice(null);
                setFlow({ step: 1, country: null, types: [] }, true);
              }}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Country
            </Button>
          }
        />
        {drafts.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            No countries configured - employees won't be able to select a
            country here until one is added.
          </p>
        ) : (
          <div className="space-y-2">
            {drafts.map((d) => (
              <CountryCard
                key={d.country}
                draft={d}
                schemes={BOTH_SCHEMES}
                onOpen={() => setViewing(d.country)}
                onRemove={() => setRemoveTarget(d.country)}
              />
            ))}
          </div>
        )}
        <Dialog
          open={viewingDraft !== null}
          onOpenChange={(o) => !o && setViewing(null)}
        >
          <DialogContent className={DETAILS_DIALOG}>
            {viewingDraft && (
              <>
                <DialogHeader>
                  <DialogTitle>{viewingDraft.country}</DialogTitle>
                  <DialogDescription>
                    Default Limits - Domestic and International, Fixed and
                    Reimbursement.
                  </DialogDescription>
                </DialogHeader>
                <CountryDetails draft={viewingDraft} schemes={BOTH_SCHEMES} />
                <DialogFooter className={PHONE_DIALOG_FOOTER}>
                  <Button variant="outline" onClick={() => setViewing(null)}>
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      const d = viewingDraft;
                      setViewing(null);
                      setNotice(null);
                      setFlow(
                        {
                          step: 2,
                          country: d.country,
                          types: configuredTravelTypes(d, BOTH_SCHEMES)
                        },
                        true
                      );
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit rates
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        <ConfirmDialog
          open={removeTarget !== null}
          onOpenChange={(o) => !o && !removing && setRemoveTarget(null)}
          title="Remove this country?"
          description={
            removeTarget
              ? `${removeTarget} will no longer be selectable by employees who rely on the Default Limits (Domestic and International, Fixed and Reimbursement).`
              : undefined
          }
          confirmLabel="Remove"
          pendingLabel="Removing…"
          pending={removing}
          destructive
          onConfirm={async () => {
            if (!removeTarget) return;
            setRemoving(true);
            try {
              await onRemoveCountry(removeTarget);
            } finally {
              setRemoving(false);
              setRemoveTarget(null);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <WizardStepper
        steps={DEFAULT_STEPS}
        currentStep={step}
        onStepClick={jumpBack(step, (n) => setFlow({ step: n }, true))}
      />
      {step > 1 && (
        <FlowContext
          parts={[country, step > 2 ? savedLabel(travelTypes) : null]}
        />
      )}
      {notice && step === 2 && (
        <Notice onDismiss={() => setNotice(null)}>{notice}</Notice>
      )}

      {step === 1 && (
        <>
          <CountryStage
            drafts={drafts}
            schemes={BOTH_SCHEMES}
            country={country}
            onPick={(c) => setFlow({ country: c, types: [] })}
          />
          <WizardFooter
            onBack={() => toOverview()}
            backLabel="Overview"
            onNext={() =>
              setFlow(
                {
                  step: 2,
                  types: configuredTravelTypes(
                    drafts.find((d) => d.country === country),
                    BOTH_SCHEMES
                  )
                },
                true
              )
            }
            nextDisabled={!country}
            nextLabel="Next: Domestic | International"
          />
        </>
      )}

      {step === 2 && draft && (
        <>
          <TravellerPickStep
            draft={draft}
            schemes={BOTH_SCHEMES}
            value={travelTypes}
            onChange={(t) => setFlow({ types: t })}
            onRemoveType={removeType}
            removingType={removingType}
          />
          <WizardFooter
            onBack={() => {
              setNotice(null);
              setFlow({ step: 1 }, true);
            }}
            backLabel="Country"
            onNext={() => setFlow({ step: 3 }, true)}
            nextDisabled={travelTypes.length === 0}
            nextLabel="Next: Fixed | Reimbursement"
          />
        </>
      )}

      {step === 3 && draft && travelTypes.length > 0 && (
        <RatesStep
          key={`${draft.country}:${travelTypes.join(",")}`}
          schemes={BOTH_SCHEMES}
          draft={draft}
          travelTypes={travelTypes}
          saving={saving}
          onBack={() => setFlow({ step: 2 }, true)}
          onSave={save}
        />
      )}
    </div>
  );
}

// ── Fixed / Reimbursement Limits: Designation → Country → Domestic | International → rate
function DesignationLimitsFlow<
  T extends FixedAllowanceEntry | ReimbursementEntry
>({
  scheme,
  entries,
  designations,
  saving,
  onSaveEntries
}: {
  scheme: PolicyScheme;
  entries: T[];
  designations: string[];
  saving: (key: string) => boolean;
  onSaveEntries: (next: T[], message: string, key: string) => Promise<boolean>;
}) {
  const categories = useCategories();
  const steps = [
    { label: "Designation" },
    { label: "Country" },
    { label: "Domestic | International" },
    { label: SCHEME_LABEL[scheme] }
  ];
  const schemes = React.useMemo(() => [scheme], [scheme]);
  const title =
    scheme === "fixed_allowance" ? "Fixed Limits" : "Reimbursement Limits";

  const [flow, setFlow] = useFlowState();
  const [notice, setNotice] = React.useState<React.ReactNode>(null);
  const [adding, setAdding] = React.useState("");
  const [removeDesignationTarget, setRemoveDesignationTarget] = React.useState<
    string | null
  >(null);
  const [removingDesignation, setRemovingDesignation] = React.useState(false);
  const [removeCountryTarget, setRemoveCountryTarget] = React.useState<{
    designation: string;
    country: string;
  } | null>(null);
  const [removingCountry, setRemovingCountry] = React.useState(false);
  // The designation open in the details dialog, and (inside it) the
  // country whose full rates are shown.
  const [viewingDesignation, setViewingDesignation] = React.useState<
    string | null
  >(null);
  const [viewingCountry, setViewingCountry] = React.useState<string | null>(
    null
  );
  const viewing = viewingDesignation;
  const setViewing = (d: string | null) => {
    setViewingDesignation(d);
    setViewingCountry(null);
  };
  const { designation, country, types: travelTypes } = flow;

  const entry = entries.find((e) => e.designation === designation) ?? null;
  // A stage is only shown once the choices it needs exist - e.g. a
  // designation removed since, or a hand-edited URL.
  let step = Math.min(flow.step, steps.length);
  if (step >= 2 && !entry) step = 1;
  if (step >= 3 && !country) step = 2;
  if (step >= 4 && travelTypes.length === 0) step = 3;

  const draftsOf = (e: T | null) =>
    e
      ? draftsFrom([
        {
          scheme,
          countries: e.countries as (CountryAmount | CountryLimits)[]
        }
      ])
      : [];
  const drafts = draftsOf(entry);
  const viewingEntry = entries.find((e) => e.designation === viewing) ?? null;
  const viewingDrafts = draftsOf(viewingEntry);
  const viewingCountryDraft =
    viewingDrafts.find((d) => d.country === viewingCountry) ?? null;
  const draft: CountryDraft | null = country
    ? (drafts.find((d) => d.country === country) ?? { country, rates: {} })
    : null;

  const withEntryCountries = (
    d: string,
    countries: (CountryAmount | CountryLimits)[]
  ) =>
    entries.map((e) => (e.designation === d ? { ...e, countries } : e)) as T[];
  const toOverview = (message?: React.ReactNode) => {
    setNotice(message ?? null);
    setFlow(
      { step: OVERVIEW, designation: null, country: null, types: [] },
      true
    );
  };

  const addDesignation = async () => {
    if (!adding) return;
    const name = adding;
    const next = [
      ...entries,
      { designation: name, countries: [] } as unknown as T
    ];
    const ok = await onSaveEntries(
      next,
      scheme === "fixed_allowance"
        ? "Allowance tier added"
        : "Reimbursement tier added",
      `${scheme}:add`
    );
    if (ok) {
      // A brand-new designation still needs its own countries - move
      // straight on to the Country stage for it.
      setAdding("");
      setNotice(<>{name} added. Now add its first country.</>);
      setFlow({ step: 2, designation: name, country: null, types: [] }, true);
    }
  };

  const removeDesignation = (d: string) =>
    onSaveEntries(
      entries.filter((e) => e.designation !== d),
      scheme === "fixed_allowance"
        ? "Allowance tier removed"
        : "Reimbursement limit removed",
      `${scheme}:remove`
    );

  const removeCountry = (d: string, c: string) => {
    const e = entries.find((x) => x.designation === d);
    if (!e) return Promise.resolve(false);
    return onSaveEntries(
      withEntryCountries(
        d,
        (e.countries as (CountryAmount | CountryLimits)[]).filter(
          (x) => x.country !== c
        )
      ),
      `${c} removed from ${d}`,
      `${scheme}:country`
    );
  };

  // Deletes one travel type's rate for this country+designation right away
  // - independent of whatever's ticked on the current step (see
  // TravellerPickStep's "Remove rate" button).
  const [removingType, setRemovingType] = React.useState<TravelType | null>(
    null
  );
  const removeType = async (t: TravelType) => {
    if (!entry || !draft) return;
    setRemovingType(t);
    try {
      const next = withTravelRates(draft, t, { [scheme]: undefined });
      const ok = await onSaveEntries(
        withEntryCountries(
          entry.designation,
          withCountry(
            entry.countries as (CountryAmount | CountryLimits)[],
            next,
            scheme,
            categories
          )
        ),
        `${title} updated for ${entry.designation}`,
        `${scheme}:country`
      );
      if (!ok) return;
      if (!hasAnyRate(next, schemes)) {
        toOverview(
          <>
            {draft.country} removed from {entry.designation} - no rates were
            left.
          </>
        );
        return;
      }
      setNotice(
        <>
          {TRAVEL_TYPE_LABEL[t]} rate removed for {draft.country} (
          {entry.designation}).
        </>
      );
      setFlow({ types: travelTypes.filter((x) => x !== t) }, true);
    } finally {
      setRemovingType(null);
    }
  };

  const save = async (rates: RatesByTravel) => {
    if (!entry || !draft) return;
    const next = applyRates(draft, rates);
    const ok = await onSaveEntries(
      withEntryCountries(
        entry.designation,
        withCountry(
          entry.countries as (CountryAmount | CountryLimits)[],
          next,
          scheme,
          categories
        )
      ),
      `${title} updated for ${entry.designation}`,
      `${scheme}:country`
    );
    if (!ok) return;
    const saved = travelTypes;
    if (!hasAnyRate(next, schemes)) {
      toOverview(
        <>
          {draft.country} removed from {entry.designation} - no rates were left.
        </>
      );
      return;
    }
    const remaining = unconfiguredTravelTypes(next, schemes);
    if (remaining.length) {
      setNotice(
        <>
          <span className="font-medium">{savedLabel(saved)}</span> rate saved
          for {draft.country} ({entry.designation}). Set{" "}
          <span className="font-medium">{savedLabel(remaining)}</span> next, or
          go back to the overview.
        </>
      );
      setFlow({ step: 3, types: remaining }, true);
      return;
    }
    toOverview(
      <>
        <span className="font-medium">{savedLabel(saved)}</span> rate saved for{" "}
        {draft.country} ({entry.designation}).
      </>
    );
  };

  if (step === OVERVIEW) {
    return (
      <div className="space-y-4">
        {notice && <Notice onDismiss={() => setNotice(null)}>{notice}</Notice>}
        <SectionBar
          title="Configured designations"
          hint="Designations without countries of their own use the Default Limits."
          action={
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => {
                setNotice(null);
                setAdding("");
                setFlow(
                  { step: 1, designation: null, country: null, types: [] },
                  true
                );
              }}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Designation
            </Button>
          }
        />
        {entries.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
            No designations yet - every designation uses the Default Limits.
          </p>
        ) : (
          <div className="space-y-2">
            {entries.map((e, i) => (
              <OverviewCard
                key={e.designation}
                title={
                  <>
                    {/* <span className="text-muted-foreground tabular-nums">
                      ({i + 1})
                    </span>{" "} */}
                    {e.designation}
                  </>
                }
                subtitle={
                  e.countries.length === 0 ? (
                    "No countries yet - uses the Default Limits"
                  ) : (
                    <>
                      <Badge
                        variant="secondary"
                        className="h-5 min-w-5 justify-center rounded-full px-1.5 font-medium tabular-nums"
                      >
                        {e.countries.length}
                      </Badge>
                      <span className="min-w-0 truncate">
                        {e.countries.length === 1 ? "country" : "countries"}:{" "}
                        {e.countries.map((c) => c.country).join(", ")}
                      </span>
                    </>
                  )
                }
                onOpen={() => setViewing(e.designation)}
                onRemove={() => setRemoveDesignationTarget(e.designation)}
                removeLabel={`Remove ${e.designation}`}
              />
            ))}
          </div>
        )}
        <Dialog
          open={viewingEntry !== null}
          onOpenChange={(o) => !o && setViewing(null)}
        >
          <DialogContent className={DETAILS_DIALOG}>
            {viewingEntry && (
              <>
                <DialogHeader>
                  <DialogTitle>{viewingEntry.designation}</DialogTitle>
                  <DialogDescription>
                    {title} - Domestic and International rates per country.
                  </DialogDescription>
                </DialogHeader>
                {!viewingCountryDraft ? (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const d = viewingEntry.designation;
                        setViewing(null);
                        setNotice(null);
                        setFlow(
                          { step: 2, designation: d, country: null, types: [] },
                          true
                        );
                      }}
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Country
                    </Button>
                  </div>
                ) : null}

                {viewingCountryDraft ? (
                  <div className="space-y-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-2 h-8"
                      onClick={() => setViewingCountry(null)}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Back
                    </Button>
                    <CountryOverviewRow
                      draft={viewingCountryDraft}
                      schemes={schemes}
                      onEdit={() => {
                        const des = viewingEntry.designation;
                        const d = viewingCountryDraft;
                        setViewing(null);
                        setNotice(null);
                        setFlow(
                          {
                            step: 3,
                            designation: des,
                            country: d.country,
                            types: configuredTravelTypes(d, schemes)
                          },
                          true
                        );
                      }}
                      onRemove={() =>
                        setRemoveCountryTarget({
                          designation: viewingEntry.designation,
                          country: viewingCountryDraft.country
                        })
                      }
                    />
                  </div>
                ) : viewingDrafts.length === 0 ? (
                  <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                    No countries yet - employees with this designation use the
                    Default Limits.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {viewingDrafts.map((d) => (
                      // No per-country delete here - Fixed/Reimbursement
                      // Limits reuse CountryCard but drop onRemove so the
                      // Trash button shows only under Default Limits (removal
                      // for this section is available from the open-details
                      // dialog's own Remove, see CountryDetails above).
                      <CountryCard
                        key={d.country}
                        draft={d}
                        schemes={schemes}
                        onOpen={() => setViewingCountry(d.country)}
                      />
                    ))}
                  </div>
                )}
                <DialogFooter className={PHONE_DIALOG_FOOTER}>
                  <Button variant="outline" onClick={() => setViewing(null)}>
                    Close
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        <ConfirmDialog
          open={removeDesignationTarget !== null}
          onOpenChange={(o) =>
            !o && !removingDesignation && setRemoveDesignationTarget(null)
          }
          title="Remove this designation?"
          description={
            removeDesignationTarget
              ? `${removeDesignationTarget} will fall back to the org-wide default ${scheme === "fixed_allowance" ? "fixed allowance" : "reimbursement limits"}.`
              : undefined
          }
          confirmLabel="Remove"
          pendingLabel="Removing…"
          pending={removingDesignation}
          destructive
          onConfirm={async () => {
            if (!removeDesignationTarget) return;
            setRemovingDesignation(true);
            try {
              await removeDesignation(removeDesignationTarget);
            } finally {
              setRemovingDesignation(false);
              setRemoveDesignationTarget(null);
            }
          }}
        />
        <ConfirmDialog
          open={removeCountryTarget !== null}
          onOpenChange={(o) =>
            !o && !removingCountry && setRemoveCountryTarget(null)
          }
          title="Remove this country?"
          description={
            removeCountryTarget
              ? `${removeCountryTarget.country} will no longer be selectable for ${removeCountryTarget.designation} under ${title}.`
              : undefined
          }
          confirmLabel="Remove"
          pendingLabel="Removing…"
          pending={removingCountry}
          destructive
          onConfirm={async () => {
            if (!removeCountryTarget) return;
            setRemovingCountry(true);
            try {
              await removeCountry(
                removeCountryTarget.designation,
                removeCountryTarget.country
              );
            } finally {
              setRemovingCountry(false);
              setRemoveCountryTarget(null);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <WizardStepper
        steps={steps}
        currentStep={step}
        onStepClick={jumpBack(step, (n) => setFlow({ step: n }, true))}
      />
      {step > 1 && (
        <FlowContext
          parts={[
            entry?.designation,
            step > 2 ? country : null,
            step > 3 ? savedLabel(travelTypes) : null
          ]}
        />
      )}
      {notice && (step === 2 || step === 3) && (
        <Notice onDismiss={() => setNotice(null)}>{notice}</Notice>
      )}

      {step === 1 && (
        <>
          {entry ? (
            <div className="space-y-4">
              <StepHeading
                title={`Designation: ${entry.designation}`}
                hint="Already configured - continue to its countries."
              />
            </div>
          ) : (
            <div className="space-y-2">
              <StepHeading
                title="Choose the designation to add"
                hint="Designations synced from Microsoft Entra; each can be added once."
              />
              <DesignationSelect
                options={designations}
                used={entries.map((e) => e.designation)}
                value={adding}
                onChange={setAdding}
              />
            </div>
          )}
          <WizardFooter
            onBack={() => toOverview()}
            backLabel="Overview"
            onNext={entry ? () => setFlow({ step: 2 }, true) : addDesignation}
            nextDisabled={entry ? false : !adding || saving(`${scheme}:add`)}
            nextLabel={
              entry
                ? "Next: Country"
                : saving(`${scheme}:add`)
                  ? "Adding…"
                  : "Add & continue"
            }
          />
        </>
      )}

      {step === 2 && entry && (
        <>
          <CountryStage
            drafts={drafts}
            schemes={schemes}
            country={country}
            onPick={(c) => setFlow({ country: c, types: [] })}
          />
          <WizardFooter
            onBack={() => {
              setNotice(null);
              setFlow({ step: 1 }, true);
            }}
            backLabel="Designation"
            onNext={() =>
              setFlow(
                {
                  step: 3,
                  types: configuredTravelTypes(
                    drafts.find((d) => d.country === country),
                    schemes
                  )
                },
                true
              )
            }
            nextDisabled={!country}
            nextLabel="Next: Domestic | International"
          />
        </>
      )}

      {step === 3 && draft && (
        <>
          <TravellerPickStep
            draft={draft}
            schemes={schemes}
            value={travelTypes}
            onChange={(t) => setFlow({ types: t })}
            onRemoveType={removeType}
            removingType={removingType}
          />
          <WizardFooter
            onBack={() => {
              setNotice(null);
              setFlow({ step: 2 }, true);
            }}
            backLabel="Country"
            onNext={() => setFlow({ step: 4 }, true)}
            nextDisabled={travelTypes.length === 0}
            nextLabel={`Next: ${SCHEME_LABEL[scheme]}`}
          />
        </>
      )}

      {step === 4 && draft && travelTypes.length > 0 && (
        <RatesStep
          key={`${entry?.designation}:${draft.country}:${travelTypes.join(",")}`}
          schemes={schemes}
          draft={draft}
          travelTypes={travelTypes}
          saving={saving(`${scheme}:country`)}
          onBack={() => setFlow({ step: 3 }, true)}
          onSave={save}
        />
      )}
    </div>
  );
}

// ── Section navigation ──────────────────────────────────────────────────
// One section visible at a time: a settings-style rail on desktop (lg+), a
// dropdown below that. Travel Duration is a single form; each limits
// section runs its OWN step-by-step flow (same stepper as the employee's
// New Request wizard):
//   Default Limits       Country → Domestic | International → Fixed | Reimbursement
//   Fixed Limits         Designation → Country → Domestic | International → Fixed
//   Reimbursement Limits Designation → Country → Domestic | International → Reimbursement
// Saving a rate saves the policy (a new version, audited, as before) and
// moves on to the other traveller type. All policy state lives in
// Policies(), so switching sections never discards an unsaved Travel
// Duration edit.
//
// The active section is kept in the `section` query param (same pattern as
// My Requests' `view`/`status`) so it survives a refresh and can be linked to.
type SectionId =
  | "duration"
  | "defaults"
  | "fixed"
  | "reimbursement"
  | "categories";

const SECTIONS: Record<
  SectionId,
  { label: string; hint: string; description: string; icon: LucideIcon }
> = {
  duration: {
    label: "Travel Duration",
    hint: "Full-day / half-day threshold",
    description:
      "Travel hours at or above this threshold count as a Full Day; anything less is a Half Day.",
    icon: Clock
  },
  defaults: {
    label: "Default Limits",
    hint: "Org-wide fallback countries",
    description:
      "Org-wide countries for any designation that has no countries of its own under Fixed Limits or Reimbursement Limits.",
    icon: Layers
  },
  fixed: {
    label: "Fixed Limits",
    hint: "Flat amount per designation",
    description: "A flat amount paid per designation - no bills required.",
    icon: Banknote
  },
  reimbursement: {
    label: "Reimbursement Limits",
    hint: "Per-category caps on bills",
    description:
      "Per-category caps on actual expenses claimed with bills, per designation.",
    icon: Coins
  },
  categories: {
    label: "Expense Categories",
    hint: "Bill categories employees pick from",
    description:
      "The categories employees choose from when uploading a Reimbursement bill, and that Reimbursement Limits sets per-category caps against.",
    icon: ReceiptText
  }
};

const SECTION_GROUPS: { label: string; items: SectionId[] }[] = [
  { label: "Rules", items: ["duration"] },
  { label: "Limits", items: ["defaults", "fixed", "reimbursement"] },
  { label: "Categories", items: ["categories"] }
];

const SECTION_IDS = Object.keys(SECTIONS) as SectionId[];
const DEFAULT_SECTION: SectionId = "duration";

/** Per-section status shown beside each nav item - a count, and a dot when
 *  the section holds an edit that hasn't been saved yet. */
type SectionStatus = Record<SectionId, { count?: string; unsaved?: boolean }>;

function SectionCount({ status }: { status: SectionStatus[SectionId] }) {
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1.5">
      {status.unsaved && (
        <span
          className="h-2 w-2 rounded-full bg-amber-500"
          title="Unsaved changes"
          aria-label="Unsaved changes"
        />
      )}
      {status.count !== undefined && (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[0.6875rem] font-medium tabular-nums text-muted-foreground">
          {status.count}
        </span>
      )}
    </span>
  );
}

/** Desktop (lg+) side rail. */
function SectionRail({
  active,
  status,
  onSelect
}: {
  active: SectionId;
  status: SectionStatus;
  onSelect: (id: SectionId) => void;
}) {
  return (
    <nav aria-label="Policy sections" className="space-y-5">
      {SECTION_GROUPS.map((group) => (
        <div key={group.label} className="space-y-1">
          <p className="px-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
            {group.label}
          </p>
          {group.items.map((id) => {
            const { label, hint, icon: Icon } = SECTIONS[id];
            const isActive = id === active;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onSelect(id)}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">
                    {label}
                  </span>
                  <span
                    className={cn(
                      "block truncate text-xs",
                      isActive ? "text-primary/80" : "text-muted-foreground"
                    )}
                  >
                    {hint}
                  </span>
                </span>
                <SectionCount status={status[id]} />
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/** Below lg: the same sections as a grouped dropdown. */
function SectionSelect({
  active,
  status,
  onSelect
}: {
  active: SectionId;
  status: SectionStatus;
  onSelect: (id: SectionId) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="policy-section" className="text-xs text-muted-foreground">
        Policy section
      </Label>
      <Select value={active} onValueChange={(v) => onSelect(v as SectionId)}>
        <SelectTrigger id="policy-section" className="h-11 w-full sm:w-80">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SECTION_GROUPS.map((group) => (
            <SelectGroup key={group.label}>
              <SelectLabel className="text-[0.6875rem] uppercase tracking-wider text-muted-foreground">
                {group.label}
              </SelectLabel>
              {group.items.map((id) => {
                const { label, icon: Icon } = SECTIONS[id];
                return (
                  <SelectItem key={id} value={id}>
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>{label}</span>
                      {status[id].count !== undefined && (
                        <span className="text-xs tabular-nums text-muted-foreground">
                          · {status[id].count}
                        </span>
                      )}
                      {status[id].unsaved && (
                        <span
                          className="h-2 w-2 rounded-full bg-amber-500"
                          aria-label="Unsaved changes"
                        />
                      )}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** One sentence per `category` with nonzero usage, pluralised, for the
 *  removal-confirmation dialog - e.g. "'Entertainment' is used in 3 policy
 *  rate(s) across 2 designation(s), and 12 existing bill(s)." Only mentions
 *  the counts that are actually nonzero, since a category used in bills but
 *  not in any current rate (or vice versa) shouldn't claim otherwise. */
function describeCategoryUsage(category: string, usage: CategoryRemovalUsage): string {
  const parts: string[] = [];
  if (usage.policyRateCount > 0) {
    const rateWord = usage.policyRateCount === 1 ? "rate" : "rates";
    let clause = `${usage.policyRateCount} policy ${rateWord}`;
    if (usage.designationCount > 0) {
      const designationWord = usage.designationCount === 1 ? "designation" : "designations";
      clause += ` across ${usage.designationCount} ${designationWord}`;
    }
    parts.push(clause);
  }
  if (usage.billCount > 0) {
    const billWord = usage.billCount === 1 ? "bill" : "bills";
    parts.push(`${usage.billCount} existing ${billWord}`);
  }
  return `"${category}" is used in ${parts.join(", and ")}.`;
}

/** Add/rename/remove the admin-configured expense-category list (see
 *  db/policy_store.py's "Expense categories are admin-configurable" module
 *  docstring section) - its own save, separate from the policy sheet's
 *  (PUT /admin/expense-categories, not /admin/policy), since this list is
 *  global/unversioned rather than part of a policy version. Removing a
 *  category that's still referenced is ALLOWED - previewCategoryRemoval
 *  gets exact usage counts first, and a confirmation dialog shows them
 *  before Save actually proceeds (skipped entirely when nothing removed is
 *  in use - no point confirming a no-op). Confirming auto-strips that
 *  category from every policy rate's limits (a new policy version, see
 *  db/policy_store.py's remove_categories_from_active_policy); existing
 *  bills keep showing whatever category they were saved with regardless. */
function ExpenseCategoriesSection({
  categories,
  onSaved
}: {
  categories: string[];
  onSaved: (categories: string[]) => void;
}) {
  const [draft, setDraft] = React.useState(categories);
  const [newCategory, setNewCategory] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Index into `draft` currently being checked (the trash icon was just
  // clicked for it) - disables that row's button so a slow network can't
  // let a second click queue up a second check for the same row.
  const [checkingIndex, setCheckingIndex] = React.useState<number | null>(
    null
  );
  // Set the MOMENT a trash-icon click's own usage check finds nonzero
  // usage for that one category - the confirmation now happens right there,
  // not deferred to Save (a later Save just persists whatever's already in
  // `draft`, which by then has already had every removal confirmed one at
  // a time). Holds the index too, not just the name, so onConfirm can still
  // remove the right row even if another row was renamed in between.
  const [pendingRemoval, setPendingRemoval] = React.useState<{
    index: number;
    category: string;
    usage: CategoryRemovalUsage;
  } | null>(null);
  const isDirty =
    draft.length !== categories.length ||
    draft.some((c, i) => c !== categories[i]);

  // Re-syncs the draft whenever the saved list changes underneath it (e.g.
  // after a successful save reloads from the server) - NOT on every
  // render, so an in-progress edit isn't wiped by this component simply
  // re-rendering for an unrelated reason.
  React.useEffect(() => setDraft(categories), [categories]);

  const renameAt = (index: number, value: string) =>
    setDraft((prev) => prev.map((c, i) => (i === index ? value : c)));
  const addCategory = () => {
    const name = newCategory.trim();
    if (!name) return;
    setDraft((prev) => [...prev, name]);
    setNewCategory("");
  };

  // Clicking the trash icon on a row that was only just ADDED in this same
  // unsaved draft (never part of the last-saved `categories`) can't be "in
  // use" anywhere yet - it has never been saved, so there is nothing to
  // check or confirm, same as before this change for every removal.
  const removeAt = async (index: number) => {
    const category = draft[index];
    if (!categories.includes(category)) {
      setDraft((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    setCheckingIndex(index);
    setError(null);
    try {
      const usage = await previewCategoryRemoval([category]);
      if (usage.policyRateCount > 0 || usage.billCount > 0) {
        setPendingRemoval({ index, category, usage });
      } else {
        setDraft((prev) => prev.filter((_, i) => i !== index));
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not check this category's usage - check your connection and try again.";
      setError(message);
      toast.error(message);
    } finally {
      setCheckingIndex(null);
    }
  };

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    setDraft((prev) => prev.filter((_, i) => i !== pendingRemoval.index));
    setPendingRemoval(null);
  };

  // Shown by the Save button itself, on top of (not instead of) each
  // individual removal already being confirmed via pendingRemoval above -
  // this one last gate covers renames/additions too, not just removals, and
  // is a plain "are you sure" (not `destructive`, since a rename/addition
  // alone isn't): confirming is what actually calls doSave below.
  const [confirmSaveOpen, setConfirmSaveOpen] = React.useState(false);

  const doSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await updateExpenseCategories(draft);
      onSaved(saved);
      setConfirmSaveOpen(false);
      toast.success("Expense categories updated");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not save these categories - check your connection and try again.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDescription = pendingRemoval
    ? describeCategoryUsage(pendingRemoval.category, pendingRemoval.usage) +
      " Policy rates will be updated to drop it. Existing bills will keep showing it, unaffected."
    : "";

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Employees pick one of these when uploading or adding a bill, and
        Reimbursement Limits sets a per-day cap against each one. Renaming a
        category here only affects NEW bills/limits from now on - an
        existing bill or rate keeps whatever name it was saved with.
      </p>
      <div className="space-y-2">
        {draft.map((category, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={category}
              onChange={(e) => renameAt(i, e.target.value)}
              className="h-9"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => removeAt(i)}
              disabled={checkingIndex !== null}
              aria-label={`Remove ${category || "this category"}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCategory();
            }
          }}
          placeholder="New category name"
          className="h-9"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0"
          onClick={addCategory}
          disabled={!newCategory.trim()}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Add category
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button
        onClick={() => setConfirmSaveOpen(true)}
        disabled={saving || !isDirty}
      >
        {saving ? "Saving…" : "Save categories"}
      </Button>
      <ConfirmDialog
        open={!!pendingRemoval}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
        title="Remove this category?"
        description={confirmDescription}
        confirmLabel="Remove category"
        destructive
        onConfirm={confirmRemoval}
      />
      <ConfirmDialog
        open={confirmSaveOpen}
        onOpenChange={(open) => !open && setConfirmSaveOpen(false)}
        title="Save these changes?"
        description="This updates the expense categories employees pick from when uploading a bill, and what Reimbursement Limits sets caps against."
        confirmLabel="Save categories"
        pending={saving}
        onConfirm={doSave}
      />
    </div>
  );
}

export default function Policies() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get("section") as SectionId | null;
  const section: SectionId =
    sectionParam && SECTION_IDS.includes(sectionParam)
      ? sectionParam
      : DEFAULT_SECTION;
  const selectSection = React.useCallback(
    (id: SectionId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("section", id);
          // Each section starts its own flow from the beginning.
          for (const key of FLOW_PARAMS) next.delete(key);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [designations, setDesignations] = React.useState<string[]>([]);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [fullDay, setFullDay] = React.useState(6);
  const [defaultFixedAllowance, setDefaultFixedAllowance] =
    React.useState<FixedAllowanceBucket>(emptyFixedBucket());
  const [defaultReimbursementLimits, setDefaultReimbursementLimits] =
    React.useState<ReimbursementBucket>(emptyReimbursementBucket());
  const [fixedAllowances, setFixedAllowances] = React.useState<
    FixedAllowanceEntry[]
  >([]);
  const [reimbursements, setReimbursements] = React.useState<
    ReimbursementEntry[]
  >([]);
  const [loading, setLoading] = React.useState(true);
  // Which single save is currently in flight - NOT a shared boolean. Every
  // section on this page funnels through the one save() below (they all
  // hit the same POST /admin/policy), so a single `saving` boolean made
  // EVERY "Save" button show "Saving…" simultaneously the moment any one of
  // them was clicked, even a field saved moments earlier - reported bug:
  // "the first two fields are already saved... it shows Saving… again."
  // Each button now compares savingKey against its OWN key instead.
  const [savingKey, setSavingKey] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [fullDayOriginal, setFullDayOriginal] = React.useState(6);
  const fullDayIsDirty = fullDay !== fullDayOriginal;

  // `preserveDuration` keeps an in-progress, unsaved Travel Duration edit in
  // the input when the reload is triggered by ANOTHER section's save - that
  // save no longer writes the duration (see save() below), so we mustn't wipe
  // what the admin typed here either. `fullDayOriginal` still tracks the real
  // saved value, so the unsaved dot stays correct. A plain page load or
  // Travel Duration's own save passes false and resets the input as usual.
  const load = React.useCallback(async (preserveDuration = false) => {
    const [designationList, categoryList, active] = await Promise.all([
      fetchDesignations(),
      fetchExpenseCategories(),
      fetchPolicy()
    ]);
    setDesignations(designationList);
    setCategories(categoryList);
    if (active) {
      if (!preserveDuration) setFullDay(active.fullDayHourThreshold);
      setFullDayOriginal(active.fullDayHourThreshold);
      setDefaultFixedAllowance(active.defaultFixedAllowance);
      setDefaultReimbursementLimits(active.defaultReimbursementLimits);
      setFixedAllowances(active.fixedAllowances);
      setReimbursements(active.reimbursements);
    }
    setLoading(false);
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  /** Saves the whole policy (a new version) with `next` applied; resolves
   *  true on success so the flows know when to move on. */
  const save = async (
    next?: {
      fullDayHourThreshold?: number;
      defaultFixedAllowance?: FixedAllowanceBucket;
      defaultReimbursementLimits?: ReimbursementBucket;
      fixedAllowances?: FixedAllowanceEntry[];
      reimbursements?: ReimbursementEntry[];
    },
    successMessage?: string,
    key?: string
  ): Promise<boolean> => {
    setSavingKey(key ?? "default");
    setError(null);
    try {
      await updatePolicy({
        // Fall back to the last SAVED threshold (fullDayOriginal), not the
        // live-edited `fullDay`. Otherwise an unsaved Travel Duration edit
        // would be silently persisted by any OTHER section's save (e.g.
        // typing 12 here, not saving it, then saving a Default Limits change
        // would write 12 too). Travel Duration's own Save passes
        // fullDayHourThreshold explicitly, so that path is unaffected.
        fullDayHourThreshold: next?.fullDayHourThreshold ?? fullDayOriginal,
        defaultFixedAllowance:
          next?.defaultFixedAllowance ?? defaultFixedAllowance,
        defaultReimbursementLimits:
          next?.defaultReimbursementLimits ?? defaultReimbursementLimits,
        fixedAllowances: next?.fixedAllowances ?? fixedAllowances,
        reimbursements: next?.reimbursements ?? reimbursements
      });
      // Only Travel Duration's own save writes the threshold - every other
      // section leaves an unsaved duration edit in place on reload.
      await load(next?.fullDayHourThreshold === undefined);
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not save this policy change - check your connection and try again.";
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setSavingKey(null);
    }
  };

  const saveFullDay = () =>
    save(
      { fullDayHourThreshold: fullDay },
      "Travel duration rule updated",
      "fullDay"
    );

  // Default Limits: one country edits BOTH default buckets at once.
  const defaultDrafts = draftsFrom([
    { scheme: "fixed_allowance", countries: defaultFixedAllowance.countries },
    {
      scheme: "reimbursement",
      countries: defaultReimbursementLimits.countries
    }
  ]);
  const saveDefaultCountry = (draft: CountryDraft) =>
    save(
      {
        defaultFixedAllowance: {
          countries: withCountry(
            defaultFixedAllowance.countries,
            draft,
            "fixed_allowance",
            categories
          )
        },
        defaultReimbursementLimits: {
          countries: withCountry(
            defaultReimbursementLimits.countries,
            draft,
            "reimbursement",
            categories
          )
        }
      },
      `Default limits updated for ${draft.country}`,
      "default:country"
    );
  const removeDefaultCountry = (country: string) =>
    save(
      {
        defaultFixedAllowance: {
          countries: defaultFixedAllowance.countries.filter(
            (c) => c.country !== country
          )
        },
        defaultReimbursementLimits: {
          countries: defaultReimbursementLimits.countries.filter(
            (c) => c.country !== country
          )
        }
      },
      `${country} removed from Default Limits`,
      "default:country"
    );

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Policy Configuration" description="" />
        <div className="flex min-h-[50vh] items-center justify-center">
          <Spinner className="size-5 text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            Loading policy…
          </span>
        </div>
      </PageContainer>
    );
  }

  const sectionStatus: SectionStatus = {
    duration: { count: `${fullDay}h`, unsaved: fullDayIsDirty },
    defaults: { count: String(defaultDrafts.length) },
    fixed: { count: String(fixedAllowances.length) },
    reimbursement: { count: String(reimbursements.length) },
    categories: { count: String(categories.length) }
  };
  const isSaving = (key: string) => savingKey === key;

  return (
    <CategoriesContext.Provider value={categories}>
    <PageContainer>
      <PageHeader
        title="Policy Configuration"
        description="Travel-duration rules, and Domestic / International allowance and reimbursement limits - org-wide and per designation."
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="lg:hidden">
        <SectionSelect
          active={section}
          status={sectionStatus}
          onSelect={selectSection}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)] xl:grid-cols-[16rem_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <div className="sticky top-4 space-y-5">
            <SectionRail
              active={section}
              status={sectionStatus}
              onSelect={selectSection}
            />
          </div>
        </aside>

        <div className="min-w-0 space-y-5">
          <Card>
            <CardHeader
              className={cn(
                section === "duration" &&
                "flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between"
              )}
            >
              <div>
                <CardTitle className="text-base">
                  {SECTIONS[section].label}
                </CardTitle>
                <CardDescription>
                  {SECTIONS[section].description}
                </CardDescription>
              </div>
              {section === "duration" && (
                <Button
                  size="sm"
                  className="self-start sm:self-auto"
                  onClick={saveFullDay}
                  disabled={savingKey === "fullDay" || !fullDayIsDirty}
                >
                  {savingKey === "fullDay" ? "Saving…" : "Save"}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {section === "duration" && (
                <div className="space-y-3">
                  <div className="max-w-xs space-y-1.5">
                    <Label htmlFor="full-day">Full day ≥ (hours)</Label>
                    <AmountInput
                      id="full-day"
                      value={fullDay}
                      onChange={setFullDay}
                      min={1}
                      max={24}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className="font-normal">
                      Full Day: {fullDay}+ hours
                    </Badge>
                    <Badge variant="outline" className="font-normal">
                      Half Day: under {fullDay} hours
                    </Badge>
                  </div>
                </div>
              )}
              {section === "defaults" && (
                <DefaultLimitsFlow
                  drafts={defaultDrafts}
                  saving={isSaving("default:country")}
                  onSaveCountry={saveDefaultCountry}
                  onRemoveCountry={removeDefaultCountry}
                />
              )}
              {section === "fixed" && (
                <DesignationLimitsFlow
                  key="fixed"
                  scheme="fixed_allowance"
                  entries={fixedAllowances}
                  designations={designations}
                  saving={isSaving}
                  onSaveEntries={(next, message, key) =>
                    save({ fixedAllowances: next }, message, key)
                  }
                />
              )}
              {section === "reimbursement" && (
                <DesignationLimitsFlow
                  key="reimbursement"
                  scheme="reimbursement"
                  entries={reimbursements}
                  designations={designations}
                  saving={isSaving}
                  onSaveEntries={(next, message, key) =>
                    save({ reimbursements: next }, message, key)
                  }
                />
              )}
              {section === "categories" && (
                <ExpenseCategoriesSection
                  categories={categories}
                  onSaved={setCategories}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContainer>
    </CategoriesContext.Provider>
  );
}
